/**
 * CardBoard Component - Kanban Board for EA-Agile Cards
 * Displays cards in columns based on their state
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import CardDetailModal from './CardDetailModal';
import NewCardModal from './NewCardModal';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface Card {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  state: string;
  priority: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface CardBoardProps {
  boardType: 'sprint' | 'backlog';
  clientId: string;
}

// Kanban columns configuration - Spec/Build pipeline
const SPRINT_COLUMNS = [
  { id: 'backlog', title: 'Backlog', bgColor: 'bg-gray-50' },
  { id: 'in_progress', title: 'In Progress', bgColor: 'bg-blue-50' },
  { id: 'ready_to_build', title: 'Ready to Build', bgColor: 'bg-yellow-50' },
  { id: 'building', title: 'Building', bgColor: 'bg-orange-50' },
  { id: 'build_review', title: 'Build Review', bgColor: 'bg-purple-50' },
  { id: 'done', title: 'Done', bgColor: 'bg-green-50' }
];

const BACKLOG_COLUMNS = [
  { id: 'icebox', title: 'Icebox', bgColor: 'bg-gray-50' },
  { id: 'grooming', title: 'Grooming', bgColor: 'bg-blue-50' },
  { id: 'ready', title: 'Ready for Sprint', bgColor: 'bg-green-50' },
  { id: 'blocked', title: 'Blocked', bgColor: 'bg-red-50' }
];

const CardBoard: React.FC<CardBoardProps> = ({ boardType, clientId }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [showNewCardModal, setShowNewCardModal] = useState(false);

  const columns = boardType === 'sprint' ? SPRINT_COLUMNS : BACKLOG_COLUMNS;

  // Fetch cards for this board
  const fetchCards = useCallback(async () => {
    if (!clientId) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call<Card>(
        API_ENDPOINTS.EA_AGILE.CARDS,
        'GET',
        undefined,
        { params: { Client_ID: clientId } }
      );

      if (isSuccessResponse(response)) {
        setCards(response.data?.Records || []);
      } else {
        throw new Error(response.message || 'Failed to fetch cards');
      }
    } catch (err) {
      console.error('Error fetching cards:', err);
      setError(err instanceof Error ? err.message : 'Failed to load cards');
    } finally {
      setIsLoading(false);
    }
  }, [boardType, clientId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Group cards by column
  const getCardsForColumn = (columnId: string): Card[] => {
    return cards.filter(card => card.state === columnId);
  };

  // Get card type color
  const getCardTypeColor = (cardType: string): string => {
    switch (cardType) {
      case 'incident':
        return 'border-l-4 border-red-500';
      case 'problem':
        return 'border-l-4 border-orange-500';
      case 'standard':
        return 'border-l-4 border-blue-500';
      case 'feature':
        return 'border-l-4 border-green-500';
      case 'research':
        return 'border-l-4 border-purple-500';
      default:
        return 'border-l-4 border-gray-500';
    }
  };

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded';
      case 'high':
        return 'bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded';
      case 'low':
        return 'bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded';
      default:
        return 'bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchCards}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FiRefreshCw className="inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Board toolbar */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {cards.length} card{cards.length !== 1 ? 's' : ''}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchCards}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white"
            >
              <FiRefreshCw className="inline mr-1" />
              Refresh
            </button>
            <button
              onClick={() => setShowNewCardModal(true)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <FiPlus className="inline mr-1" />
              New Card
            </button>
          </div>
        </div>
      </div>

      {/* Kanban columns */}
      <div className="flex-1 overflow-x-auto">
        <div className="h-full flex p-4 space-x-4 min-w-max">
          {columns.map((column) => {
            const columnCards = getCardsForColumn(column.id);

            return (
              <div
                key={column.id}
                className="flex-shrink-0 w-80 flex flex-col"
              >
                {/* Column header */}
                <div className={`${column.bgColor} px-3 py-2 rounded-t-lg border border-gray-200`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">{column.title}</h3>
                    <span className="text-sm text-gray-600">{columnCards.length}</span>
                  </div>
                </div>

                {/* Cards container */}
                <div className="flex-1 bg-white border-x border-b border-gray-200 rounded-b-lg p-2 space-y-2 overflow-y-auto">
                  {columnCards.length === 0 ? (
                    <div className="text-center text-sm text-gray-400 py-8">
                      No cards
                    </div>
                  ) : (
                    columnCards.map((card) => (
                      <div
                        key={card.card_id}
                        onClick={() => setSelectedCard(card)}
                        className={`bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${getCardTypeColor(card.card_type)}`}
                      >
                        {/* Card header */}
                        <div className="flex items-start justify-between mb-2">
                          <span className={getPriorityBadge(card.priority)}>
                            {card.priority}
                          </span>
                          <span className="text-xs text-gray-500">
                            {card.card_type}
                          </span>
                        </div>

                        {/* Card title */}
                        <h4 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">
                          {card.title}
                        </h4>

                        {/* Card description */}
                        {card.description && (
                          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                            {card.description}
                          </p>
                        )}

                        {/* Card footer */}
                        <div className="text-xs text-gray-500 flex items-center justify-between pt-2 border-t border-gray-100">
                          <span>{card.created_by}</span>
                          <span>{new Date(card.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          clientId={clientId}
        />
      )}

      {/* New Card Modal */}
      {showNewCardModal && (
        <NewCardModal
          onClose={() => setShowNewCardModal(false)}
          onSuccess={fetchCards}
          defaultState={boardType === 'sprint' ? 'backlog' : 'icebox'}
          clientId={clientId}
        />
      )}
    </div>
  );
};

export default CardBoard;
